import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface ViduQ1Params {
  /**
   * 文本提示词，必填，不能超过1500个字符
   */
  prompt: string;
  /**
   * 风格，默认值为'general'
   * @values general | anime
   */
  style?: 'general' | 'anime';
  /**
   * 随机种子，0表示使用随机数
   */
  seed?: number;
  /**
   * 比例，默认值为'16:9'
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 运动幅度，默认值为'auto'
   * @values auto | small | medium | large
   */
  movement_amplitude?: 'auto' | 'small' | 'medium' | 'large';
  /**
   * 是否添加背景音乐，默认值为False
   */
  bgm?: boolean;
  /**
   * 透传参数，最多1048576个字符
   */
  payload?: string;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback_url?: string;
}

export interface ViduQ1Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * vidu_q1
 *
 * <p>通过Vidu Q1 API创建视频生成任务。<a href="https://platform.vidu.com/docs/text-to-video">API Documentation</a></p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, viduQ1 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await viduQ1({
 *   // 参数见 ViduQ1Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 ViduQ1Params 接口定义
 * @returns 生成的资源列表
 */
export async function viduQ1(
  params: ViduQ1Params,
): Promise<ViduQ1Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'vidu_q1',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: ViduQ1Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
